import React, { memo, useRef, useEffect } from "react";
import { 
  Box, 
  ActionIcon, 
  Avatar,
  Text,
  Flex
} from "@mantine/core";
import { IconX } from "@tabler/icons-react";
import { useAutoHeight } from "../../hooks/useAutoHeight";
import { useWritingMode } from "../../contexts/WritingModeContext";
import { MAX_TEXT_LENGTH, renderTextWithColor } from "../../utils/textColorUtils";
import ThreadConnectionLine from "./ThreadConnectionLine";
import ThreadTextCounter from "./ThreadTextCounter";

const ThreadInput = ({ 
  thread, 
  onTextChange, 
  onRemove, 
  loading, 
  userProfile,
  showRemoveButton = true,
  placeholder = "What's new?",
  isLast = false,
  readOnly = false,
  showConnectionLine = true
}) => {
  const { writingMode } = useWritingMode();
  const { textAreaRef, adjustHeight } = useAutoHeight(thread.text, thread.isMain, [writingMode]);
  
  // 스크롤 위치 보존을 위한 ref
  const scrollPositionRef = useRef({ top: 0, left: 0 });
  
  // thread.text가 변경될 때 스크롤 위치 보존
  useEffect(() => {
    const textarea = textAreaRef.current;
    if (textarea) {
      // 스크롤 위치 복원
      textarea.scrollTop = scrollPositionRef.current.top;
      textarea.scrollLeft = scrollPositionRef.current.left;
    }
  }, [thread.text]);

  // onChange에서 스크롤 위치 저장
  const handleChange = (e) => {
    const textarea = e.target;
    // 현재 스크롤 위치 저장
    scrollPositionRef.current.top = textarea.scrollTop;
    scrollPositionRef.current.left = textarea.scrollLeft;
    
    onTextChange(thread.id, e.target.value);
  };

  return (
    <Box
      style={{
        width: "100%",
        position: "relative",
      }}
    >
      <Flex gap="12px" align="flex-start" style={{ minHeight: "120px" }}>
        {/* 프로필 이미지와 연결선 컨테이너 */}
        <Box style={{ flexShrink: 0, height: "100%" }}>
          {showConnectionLine && (
            <ThreadConnectionLine isMain={thread.isMain} isLast={isLast} />
          )}
          
          <Avatar
            src={userProfile?.threads_profile_picture_url}
            alt={userProfile?.threads_username || "User"}
            size={36}
            radius="xl"
            style={{ position: "relative", zIndex: 1 }}
          />
        </Box>

        {/* 메인 입력 영역 */}
        <Box style={{ 
          flex: 1, 
          height: "100%",
          paddingBottom: isLast ? "0" : "24px",
          position: "relative"
        }}>
          {/* 사용자명 (메인 스레드만) */}
          {thread.isMain && (
            <Text 
              size="sm" 
              weight={600} 
              mb={4}
              style={{ color: "#000" }}
            >
              {userProfile?.threads_username || "사용자"}
            </Text>
          )}

          {/* 텍스트 입력 영역 */}
          <Box style={{ position: "relative" }}>
          <textarea
            ref={textAreaRef}
            value={thread.text}
            onChange={readOnly ? undefined : handleChange}
            disabled={loading || readOnly}
            placeholder={thread.isMain ? placeholder : "Say more..."}
            style={{
              width: "100%",
              border: "none",
              outline: "none",
              backgroundColor: "transparent",
              fontSize: thread.isMain ? "16px" : "15px",
              lineHeight: "1.4",
              fontFamily: "inherit",
              minHeight: thread.isMain ? "120px" : "80px",
              padding: "0",
              margin: "0",
              resize: "none",
              overflow: "auto", // hidden에서 auto로 변경하여 스크롤 허용
              wordWrap: "break-word",
              whiteSpace: "pre-wrap",
              cursor: loading || readOnly ? "default" : "text",
              position: "relative",
              color: "transparent",
              caretColor: readOnly ? "transparent" : "#000"
            }}
          />
            
            {/* 색상이 적용된 텍스트 오버레이 */}
            <div
              style={{
                position: "absolute",
                top: "0",
                left: "0",
                width: "100%",
                height: "100%",
                pointerEvents: "none",
                fontSize: thread.isMain ? "16px" : "15px",
                lineHeight: "1.4",
                fontFamily: "inherit",
                padding: "0",
                margin: "0",
                wordWrap: "break-word",
                whiteSpace: "pre-wrap",
                overflow: "hidden"
              }}
            >
              {renderTextWithColor(thread.text, MAX_TEXT_LENGTH)}
            </div>
          </Box>
          
          <ThreadTextCounter 
            text={thread.text}
            maxLength={MAX_TEXT_LENGTH}
          />

          {/* 삭제 버튼 */}
          {showRemoveButton && !thread.isMain && (
            <ActionIcon
              size="sm"
              color="gray"
              variant="subtle"
              onClick={() => onRemove(thread.id)}
              style={{
                position: "absolute",
                top: "0px",
                right: "0px"
              }}
            >
              <IconX size={14} />
            </ActionIcon>
          )}
        </Box>
      </Flex>
    </Box>
  );
};

export default memo(ThreadInput); 
